import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Space, Avatar } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import storageService from '../../services/storageService';
import { useAuth } from '../../context/AuthContext';
import { hasAccessToModule, MODULES } from '../../models/permissions';
import './Layout.css';

const { Header, Sider, Content, Footer } = Layout;

export const AppLayout = ({ children, collapsed, setCollapsed }) => {
  // Récupérer le nom et logo de l'application depuis Supabase
  const [appName, setAppName] = useState('Gestion EAU');
  const [appLogo, setAppLogo] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Charger les paramètres au montage du composant
  useEffect(() => {
    const loadSettings = async () => {
      const name = await storageService.getSetting('app_name', 'Gestion EAU');
      const logo = await storageService.getSetting('app_logo', '');
      setAppName(name);
      setAppLogo(logo);
    };
    loadSettings();
  }, []);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/login');
    }
  };

  const menuItems = [
    {
      key: 'dashboard',
      label: <Link to="/">Tableau de bord</Link>,
      icon: '📊'
    },
    // Show subscribers if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.SUBSCRIBERS) ? [{
      key: 'subscribers',
      label: <Link to="/subscribers">Abonnés</Link>,
      icon: '👥'
    }] : []),
    // Show meters if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.METERS) ? [{
      key: 'meters',
      label: <Link to="/meters">Compteurs</Link>,
      icon: '⚙️'
    }] : []),
    // Show readings if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.READINGS) ? [{
      key: 'readings',
      label: <Link to="/readings">Relevés</Link>,
      icon: '📈'
    }] : []),
    // Show billing if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.BILLING) ? [{
      key: 'billing',
      label: <Link to="/billing">Facturation</Link>,
      icon: '📋'
    }] : []),
    // Show payments if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.PAYMENTS) ? [{
      key: 'payments',
      label: <Link to="/payments">Paiements</Link>,
      icon: '💳'
    }] : []),
    // Show reports if user has access
    ...(hasAccessToModule(user?.permissions, MODULES.REPORTS) ? [{
      key: 'reports',
      label: <Link to="/reports">Rapports</Link>,
      icon: '📊'
    }] : []),
    // Show settings only for admins
    ...(user?.role === 'admin' ? [{
      key: 'settings',
      label: <Link to="/settings">Configuration</Link>,
      icon: '⚙️'
    }] : []),
    {
      key: 'profile',
      label: <Link to="/profile">Mon Profil</Link>,
      icon: '👤'
    },
    // Show manage-users only for admins
    ...(user?.role === 'admin' ? [{
      key: 'manage-users',
      label: <Link to="/manage-users">Gestion Utilisateurs</Link>,
      icon: '👨‍💼'
    }] : []),
    // Show audit-logs only for admins
    ...(user?.role === 'admin' ? [{
      key: 'audit-logs',
      label: <Link to="/audit-logs">Journal d'Activité</Link>,
      icon: '📋'
    }] : [])
  ];

  const userMenu = {
    items: [
      {
        key: 'profile',
        label: `${user?.firstName} ${user?.lastName}`,
        icon: <UserOutlined />,
        disabled: true
      },
      {
        key: 'email',
        label: user?.email,
        disabled: true,
        style: { fontSize: '12px', color: '#666' }
      },
      {
        key: 'role',
        label: `Rôle: ${user?.role}`,
        disabled: true,
        style: { fontSize: '12px', color: '#666' }
      },
      {
        type: 'divider'
      },
      {
        key: 'logout',
        label: 'Déconnexion',
        icon: <LogoutOutlined />,
        onClick: handleLogout
      }
    ]
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} width={250}>
        <div className="logo">
          {appLogo && !collapsed && (
            <img src={appLogo} alt="Logo" style={{ height: '40px', marginRight: '10px', verticalAlign: 'middle' }} />
          )}
          <h2 style={{ display: 'inline-block', margin: 0, verticalAlign: 'middle' }}>
            {collapsed ? appName.substring(0, 3) : appName}
          </h2>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={['dashboard']}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header className="app-header">
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
            <h1 style={{ margin: 0 }}>Gestion des Factures - Eau Potable</h1>
          </Space>
          <Space>
            <span style={{ color: '#fff' }}>
              Bienvenue {user?.firstName}
            </span>
            <Dropdown menu={userMenu} trigger={['click']}>
              <Avatar
                style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
          {children}
        </Content>
        <Footer style={{ textAlign: 'center' }}>
          © 2026 - Gestion des Factures d'Eau Potable - Tous droits réservés
        </Footer>
      </Layout>
    </Layout>
  );
};
